import React, { useState, useEffect } from 'react';
import { Home as HomeIcon, Users, User, Table2, Star, Wrench, Settings as SettingsIcon, Upload, Trophy, Sparkles, CalendarRange, Timer } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { getSsgSessions } from '../api';
import { SSG_COMPETITION_NAME } from '../ssgConstants';

// Individual report was ungated from when it was first built (never lived in tabs.js's
// ALL_TABS/visibleTabs permission list, just an unconditional button) — fixed 2026-08-12: now in
// tabs.js's HOME_ONLY_TABS with a Settings checkbox, gated: true below, same as Team table/
// Trainer ratings. Previously this meant anyone with 'home' access saw it regardless of their
// own allowedTabs; existing non-admin accounts lose that automatic access until re-granted.
// Team table / Trainer ratings / Upload zone
// USED to be gated in the old top-nav via visibleTabs' `user.isAdmin || allowedTabs.includes(id)`
// check (App.js) — now that they also live here, that same check has to move with them or staff
// without those tabs granted would silently gain access. team360table/trainerratings' own
// tabs.js entries got dropped when they moved here (Stage 6a) even though this gate kept
// checking for them — Settings' admin UI had no checkbox to grant/revoke them at all. Fixed
// 2026-07-25: both are back in tabs.js's ALL_TABS with these exact ids, so Settings' Tab Access
// grid can manage them again. Settings is deliberately ungated, mirroring tabs.js's own special
// case for it (`t.id === 'settings' || user.isAdmin || ...` — settings is always visible to
// everyone regardless of allowedTabs). Squad builder (Stage 7b) was left ungated at launch (no
// pre-existing tabs.js/allowedTabs entry to carry forward) — fixed 2026-07-29, now in tabs.js's
// HOME_ONLY_TABS with a Settings checkbox, same gate as Team table/Trainer ratings.
// Home itself is gated as of 2026-07-29 too (confirmed default: only admins + explicitly-
// granted accounts like Vincent keep it — see tabs.js's HOME_ONLY_TABS comment and App.js's
// AppShell, where activePage's initial value falls back to 'overview' for everyone else).
// NOTE: flipping lineup-builder/home to gated flips existing behavior for any non-admin
// account that was relying on their previous unconditional access — an admin needs to
// explicitly grant them in Settings for those accounts to keep using them.
// 'einstein' (2026-09-03) routes to the dedicated full-page Einstein tab (pages/EinsteinChat.js)
// — a Claude-like single-conversation chat, distinct from the compact console embedded in
// Home's lower half (EinsteinConsole.js), which stays for quick questions without leaving Home.
// Deliberately co-exists with Squad Builder's unrelated same-named button (LineupBuilder.js,
// "Einstein" ranking auto-fill, unaffected by this) — a naming collision accepted deliberately
// (2026-09-03 decision) rather than renaming either.
// Gated on its OWN 'einstein' permission as of 2026-09-10 (explicit request) — previously rode
// 'home's gate ("same underlying capability, not a separate permission"), which meant anyone
// with Home access saw it automatically. Now a real tabs.js entry (HOME_ONLY_TABS), defaulted
// off for new non-admin accounts (Settings.js's DEFAULT_NEW_USER_TABS) so it can be granted
// per-account from Settings as the feature gets tested more, same mechanism as every other tab.
const NAV_ITEMS = [
  { id: 'home', label: 'Home', icon: HomeIcon, gated: true },
  { id: 'einstein', label: 'Einstein', icon: Sparkles, gated: true },
  // Club Planner (2026-10-02): first in the right-hand group. Also in the main tab bar (tabs.js),
  // for accounts without Home access. Edit rights imply seeing it, same as App.js and the server.
  { id: 'planner', label: 'Planner', icon: CalendarRange, gated: true, alsoGrantedBy: ['planner-edit'] },
  // Match Day (2026-10-18): only here, not in the main tab bar. onNavigate('matchday') opens the
  // full-screen /matchday route (App.js's handlePageChange).
  { id: 'matchday', label: 'Match Day', icon: Timer, gated: true },
  { id: 'individualreport', label: 'Individual report', icon: Users, gated: true },
  { id: 'team360table', label: 'Team table', icon: Table2, gated: true },
  { id: 'trainerratings', label: 'Trainer ratings', icon: Star, gated: true },
  { id: 'lineup-builder', label: 'Squad builder', icon: Wrench, gated: true },
  { id: 'ssg', label: SSG_COMPETITION_NAME, icon: Trophy, gated: true },
  { id: 'gpsupload', label: 'Upload zone', icon: Upload, gated: true },
  { id: 'players', label: 'Player profiles', icon: User, gated: true },
  { id: 'settings', label: 'Settings', icon: SettingsIcon, gated: false },
];

// A draft session whose date is more than 24h old is the single biggest failure mode of Period
// League (per Stage 3's design brief) — surfaced as a small dot on the nav pill from wherever
// staff happen to be, not just on the SSG pages themselves. Checked once per TopBar mount
// (every hideHeader page renders this bar), not polled — cheap enough at this squad/session
// scale, and staleness only changes day-to-day, not minute-to-minute.
function useHasStaleSsgDraft() {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getSsgSessions({ requesterRole: 'staff' }).then(rows => {
      if (cancelled) return;
      const cutoff = Date.now() - 24 * 3600 * 1000;
      setStale((rows || []).some(s => s.status === 'draft' && new Date(s.session_date).getTime() < cutoff));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return stale;
}

// Decorative stadium chrome from the "Team KPI Dashboard" design handoff — floodlight
// pylons top-left/top-right with a flickering lamp head, and a jagged stadium-tier
// silhouette pinned to the bottom, fading to solid. Purely decorative/pointer-events:none.
function StadiumBackdrop({ isDay }) {
  const tierColor = isDay ? 'rgba(20,24,28,0.06)' : 'rgba(255,255,255,0.05)';
  const lampGlow = isDay ? 'rgba(8,145,178,0.55)' : 'rgba(34,211,238,0.75)';
  const pylonColor = isDay ? 'rgba(20,24,28,0.14)' : 'rgba(255,255,255,0.12)';
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
      <style>{`@keyframes floodflicker{0%,100%{opacity:1}92%{opacity:1}93%{opacity:.55}95%{opacity:1}97%{opacity:.7}98%{opacity:1}}`}</style>
      {[{ side: 'left', x: 18 }, { side: 'right', x: 18 }].map(p => (
        <div key={p.side} style={{ position: 'absolute', top: 0, [p.side]: p.x, width: 4, height: 130, background: pylonColor }}>
          <div style={{
            position: 'absolute', top: -6, left: -8, width: 20, height: 10, borderRadius: 3,
            background: lampGlow, boxShadow: `0 0 24px 6px ${lampGlow}`,
            animation: 'floodflicker 5s ease-in-out infinite',
          }} />
        </div>
      ))}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: 90,
        clipPath: 'polygon(0% 40%, 8% 30%, 16% 42%, 24% 28%, 32% 40%, 40% 26%, 48% 38%, 56% 24%, 64% 36%, 72% 22%, 80% 34%, 88% 20%, 96% 32%, 100% 24%, 100% 100%, 0% 100%)',
        background: `repeating-linear-gradient(0deg, ${tierColor} 0px, ${tierColor} 2px, transparent 2px, transparent 10px), ${isDay ? 'rgba(20,24,28,0.05)' : 'rgba(255,255,255,0.04)'}`,
      }} />
    </div>
  );
}

// Mirrors App.js's AppShell header title exactly (same font stack/weight/style, "HeraLab" +
// blue "X") per explicit request to match the physical dashboard's top bar, just recolored
// per this chrome's own night/day tokens instead of App.js's var(--text-primary) since this
// background is its own gradient, not var(--bg-app).
function TopBar({ isDay, onNavigate, user }) {
  const textColor = isDay ? '#1c2024' : '#fff';
  const pillBg = isDay ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.06)';
  const pillBorder = isDay ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)';
  const accent = isDay ? '#0891b2' : '#22d3ee';

  const visibleItems = NAV_ITEMS.filter(item => {
    if (!item.gated) return true;
    if (!user) return false;
    const tabs = user.allowedTabs || [];
    return user.isAdmin || tabs.includes(item.id) || (item.alsoGrantedBy || []).some(id => tabs.includes(id));
  });
  // Home and Einstein sit right next to the logo (per explicit request, 2026-07-22, extended to
  // Einstein 2026-09-03) rather than grouped with the rest — everything else stays in the
  // right-aligned pill group.
  const homeItem = visibleItems.find(item => item.id === 'home');
  const einsteinItem = visibleItems.find(item => item.id === 'einstein');
  const rightItems = visibleItems.filter(item => item.id !== 'home' && item.id !== 'einstein');
  const hasStaleSsgDraft = useHasStaleSsgDraft();

  const NavPill = ({ id, label, icon: Icon, highlight }) => (
    <button key={id} onClick={() => onNavigate(id)}
      style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 999,
        border: `1px solid ${highlight ? accent : pillBorder}`,
        background: highlight ? (isDay ? '#0f2430' : 'rgba(34,211,238,0.14)') : pillBg,
        backdropFilter: 'blur(10px)',
        color: highlight && isDay ? '#eaf6fa' : textColor, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = accent; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = highlight ? accent : pillBorder; }}
    >
      {highlight && <span style={{ width: 6, height: 6, borderRadius: 999, background: accent, flex: 'none' }} />}
      <Icon size={14} color={accent} /> {label}
      {id === 'ssg' && hasStaleSsgDraft && (
        <span
          title="A draft session is more than 24h old"
          style={{ position: 'absolute', top: -3, right: -3, width: 10, height: 10, borderRadius: '50%', background: '#ef4444', border: '2px solid ' + (isDay ? '#eef1f3' : '#08090b') }}
        />
      )}
    </button>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, padding: '4px 4px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 900, fontSize: 22, fontStyle: 'italic', fontFamily: "'Arial Black', Impact, sans-serif", letterSpacing: '-0.02em', whiteSpace: 'nowrap', color: textColor }}>
          HeraLab<span style={{ color: '#2563eb' }}>X</span>
        </div>
        {homeItem && <NavPill {...homeItem} />}
        {einsteinItem && <NavPill {...einsteinItem} highlight />}
      </div>
      <nav style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {rightItems.map(item => <NavPill key={item.id} {...item} />)}
      </nav>
    </div>
  );
}

// Shared page shell for Home + the pages reachable from Home's own nav (Team table, Trainer
// ratings) — the stadium-gradient background, decorative chrome, and the HeraLabX top bar
// nav, all in one place so those pages "stay in the same layout as Home" (2026-07-22) rather
// than each reimplementing it. Explicitly NOT used by every page — the rest of the app keeps
// App.js's original header/tab-bar shell; only pages that opt into hideHeader in App.js use
// this instead.
export default function PageChrome({ onNavigate, user, contentMaxWidth, padding = '20px 24px 28px 44px', children }) {
  const { theme } = useTheme();
  const isDay = theme === 'day';

  return (
    <div style={{
      // App.js's <main> wrapper pads every side by 24px; -24px margin here cancels that
      // exactly so this bleeds to the true viewport edges (see Home.js's original fix,
      // 2026-07-22) — box-sizing:border-box is global (index.css) so the padding below
      // doesn't reopen that gap. overflowX (not overflow) hidden: a table page's content can
      // legitimately be taller than 100vh and must still scroll, only horizontal bleed from
      // the decorative chrome needs clipping.
      position: 'relative', minHeight: '100vh', margin: '-24px', padding,
      background: isDay
        ? 'linear-gradient(180deg, #eef1f3, #e3e7ea 55%, #d7dce0)'
        : 'linear-gradient(180deg, #0c0e11, #08090b 55%, #050607)',
      overflowX: 'hidden',
    }}>
      <StadiumBackdrop isDay={isDay} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <TopBar isDay={isDay} onNavigate={onNavigate} user={user} />
        <div style={{ marginTop: 24, ...(contentMaxWidth ? { maxWidth: contentMaxWidth, margin: '24px auto 0' } : {}) }}>
          {children}
        </div>
      </div>
    </div>
  );
}
